import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, Grid } from '@mui/material';

import * as TStyle from 'commons/components/Table/Table.styles';
import { STOItemStatusEnum } from 'commons/enums';
import { STODocument, STOItem } from 'commons/types';
import { RowTitleProps } from 'commons/types/listPageData.interface';
import { DataTable } from 'pages/dashboard/commons/components';
import { getSTOItemListAction } from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setAllSTOItemList } from 'redux-stores/reducers/dashboard.reducer';
import { AppDispatch } from 'redux-stores/store';

import S from '../styles/STOItemDialogPage.style';

const rowTitles: RowTitleProps[] = [
  { title: 'SKU Code' },
  { title: 'Name Description' },
  { title: 'Quantity' },
  { title: 'Status' },
  { title: 'Picker' },
  { title: 'Receiver' },
  { title: 'Error Receive Message' },
];

const TableRow: React.FC<{
  data: STOItem;
  index: number;
}> = ({ data, index }) => {
  return (
    <TStyle.TableRow>
      <TStyle.TableColumn
        style={{ width: '15%' }}
        data-testid={`STOItemRow-${index + 1}`}
      >
        {data.sku_code}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '20%' }}
        data-testid={`STOItemRow-${index + 1}`}
      >
        {data.name}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '10%' }}
        data-testid={`STOItemRow-${index + 1}`}
      >
        {data.quantity}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '15%' }}
        data-testid={`STOItemRow-${index + 1}`}
      >
        {data.status}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '10%' }}
        data-testid={`STOItemRow-${index + 1}`}
      >
        {data.picker ? data.picker.split('@')[0] : '-'}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '10%' }}
        data-testid={`STOItemRow-${index + 1}`}
      >
        {data.receiver ? data.receiver.split('@')[0] : '-'}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '15%' }}
        data-testid={`STOItemRow-${index + 1}`}
      >
        {data.status === STOItemStatusEnum.RECEIVE_UPDATE_FAILED
          ? data.error_receiving_reason || '-'
          : '-'}
      </TStyle.TableColumn>
    </TStyle.TableRow>
  );
};

const STOItemDialogPage: React.FC<{
  stoDocument: STODocument | null;
  onClose: () => void;
}> = ({ stoDocument, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { stoItemList } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );
  const [showDialog, setShowDialog] = useState<boolean>(false);

  useEffect(() => {
    if (stoDocument !== null) {
      // fetch sto item
      dispatch(getSTOItemListAction(stoDocument.sto_number));
    }
  }, [stoDocument, dispatch]);

  useEffect(() => {
    if (stoItemList.list.length === 0) {
      setShowDialog(false);
      if (stoItemList.sto_number !== '-') {
        dispatch(setAllSTOItemList({ sto_number: '-', list: [] }));
      }
    } else {
      setShowDialog(true);
    }
  }, [stoItemList]);

  return (
    <Dialog
      open={!!stoDocument && showDialog}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: '80vw', width: '80vw', maxHeight: '80vh' },
      }}
    >
      {/* Title */}
      <S.TitleWrapper container>
        <Grid item xs={12}>
          <b>STO Item Detail</b>
        </Grid>
      </S.TitleWrapper>

      {/* Content Document Information */}
      <S.DocumentInformationWrapper container>
        <Grid item xs={3}>
          <S.DocumentInfoTitle>Document Number</S.DocumentInfoTitle>
          <S.DocumentInfoValue>
            <b>{stoDocument?.sto_number || '-'}</b>
          </S.DocumentInfoValue>
        </Grid>
        <Grid item xs={3}>
          <S.DocumentInfoTitle>Status</S.DocumentInfoTitle>
          <S.DocumentInfoValue>
            <b>{stoDocument?.status || '-'}</b>
          </S.DocumentInfoValue>
        </Grid>
        <Grid item xs={3}>
          <S.DocumentInfoTitle>Total SKUs</S.DocumentInfoTitle>
          <S.DocumentInfoValue>
            <b>{stoDocument?.total_sku || '-'}</b>
          </S.DocumentInfoValue>
        </Grid>
        <Grid item xs={3}>
          <S.DocumentInfoTitle>Total QTY</S.DocumentInfoTitle>
          <S.DocumentInfoValue>
            <b>{stoDocument?.total_qty || '-'}</b>
          </S.DocumentInfoValue>
        </Grid>
      </S.DocumentInformationWrapper>

      {/* STO Item List Tables */}
      <S.TableSection>
        <DataTable rowTitles={rowTitles} hideCheckbox>
          <TStyle.TableBody>
            {stoItemList.list.map((item, index) => (
              <TableRow data={item} index={index} />
            ))}
          </TStyle.TableBody>
        </DataTable>
      </S.TableSection>
    </Dialog>
  );
};

export default STOItemDialogPage;
